import React, { useState, useEffect } from 'react';
import {
  Stack,
  Title,
  Paper,
  NumberInput,
  Button,
  Group,
  Text,
  Alert,
  Divider,
  Grid,
  Card,
  Loader,
  Center
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconSettings,
  IconUsers,
  IconClock,
  IconInfoCircle,
  IconDeviceFloppy
} from '@tabler/icons-react';
import { batchService } from '../../services/batchService';

const BatchSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  const form = useForm({
    initialValues: {
      default_max_batch_size: 50,
    },
    validate: {
      default_max_batch_size: (value) => {
        if (!value || value < 1) return 'Batch size must be at least 1';
        if (value > 1000) return 'Batch size cannot exceed 1000';
        return null;
      },
    },
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await batchService.getSettings();
      setSettings(data);
      form.setValues({
        default_max_batch_size: data.default_max_batch_size,
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load batch settings',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const updatedSettings = await batchService.updateSettings(values);
      setSettings(updatedSettings);
      notifications.show({
        title: 'Success',
        message: 'Batch settings updated successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update batch settings',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    form.setValues({
      default_max_batch_size: 50,
    });
  };

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack>
      <Group>
        <IconSettings size={32} />
        <Title order={1}>Batch Settings</Title>
      </Group>

      <Alert icon={<IconInfoCircle size="1rem" />} color="blue">
        These settings control the default values used when creating new batches automatically.
        They apply to batches created through the webhook payment system and the "Create Next Batch" feature.
      </Alert>

      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Paper withBorder p="lg">
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                <Title order={3}>Default Batch Configuration</Title>

                <NumberInput
                  label="Default Maximum Batch Size"
                  description="Maximum number of members that can be assigned to a new batch"
                  placeholder="Enter default batch size"
                  min={1}
                  max={1000}
                  leftSection={<IconUsers size={16} />}
                  required
                  {...form.getInputProps('default_max_batch_size')}
                />

                <Divider />

                <Group justify="space-between">
                  <Button
                    variant="light"
                    onClick={resetToDefault}
                  >
                    Reset to Default
                  </Button>
                  <Button
                    type="submit"
                    loading={saving}
                    leftSection={<IconDeviceFloppy size={16} />}
                  >
                    Save Settings
                  </Button>
                </Group>
              </Stack>
            </form>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack>
            <Card withBorder p="md">
              <Stack gap="xs">
                <Group>
                  <IconUsers size={20} color="var(--mantine-color-blue-6)" />
                  <Text fw={600} size="sm">Current Batch Size</Text>
                </Group>
                <Text size="xl" fw={700}>
                  {settings?.default_max_batch_size || 0}
                </Text>
                <Text size="xs" c="dimmed">
                  Members per batch
                </Text>
              </Stack>
            </Card>

            {/* <Card withBorder p="md">
              <Stack gap="xs">
                <Group>
                  <IconClock size={20} color="var(--mantine-color-green-6)" />
                  <Text fw={600} size="sm">Current Cap Time</Text>
                </Group>
                <Text size="xl" fw={700}>
                  {settings?.default_cap_time_hours || 0}h
                </Text>
                <Text size="xs" c="dimmed">
                  Hours after start
                </Text>
              </Stack>
            </Card> */}

            <Paper withBorder p="md">
              <Stack gap="xs">
                <Text fw={600} size="sm">How it works:</Text>
                <Text size="xs" c="dimmed">
                  • New batches use these default values
                </Text>
                <Text size="xs" c="dimmed">
                  • Existing batches are not affected
                </Text>
                <Text size="xs" c="dimmed">
                  • Individual batches can be edited manually
                </Text>
                <Text size="xs" c="dimmed">
                  • Cap time is calculated from batch start time
                </Text>
              </Stack>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>

      <Paper withBorder p="lg">
        <Stack>
          <Title order={4}>Batch Creation Examples</Title>
          <Text size="sm" c="dimmed">
            Based on current settings, here's how new batches will be configured:
          </Text>

          <Grid>
            <Grid.Col span={6}>
              <Paper withBorder p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Stack gap="xs">
                  <Text fw={600} size="sm">If batch starts tomorrow at 11:00 AM:</Text>
                  <Text size="xs">
                    • Max members: {form.values.default_max_batch_size}
                  </Text>
                  <Text size="xs">
                    • Enrollment stops: {
                      new Date(Date.now() + 24 * 60 * 60 * 1000 + form.values.default_cap_time_hours * 60 * 60 * 1000)
                        .toLocaleString('en-IN', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                    }
                  </Text>
                </Stack>
              </Paper>
            </Grid.Col>
            <Grid.Col span={6}>
              <Paper withBorder p="sm" style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                <Stack gap="xs">
                  <Text fw={600} size="sm">Enrollment window:</Text>
                  <Text size="xs">
                    Duration: {form.values.default_cap_time_hours} hours
                  </Text>
                  <Text size="xs">
                    {form.values.default_cap_time_hours < 24 && "Same day enrollment"}
                    {form.values.default_cap_time_hours === 24 && "24-hour enrollment window"}
                    {form.values.default_cap_time_hours > 24 && `${Math.floor(form.values.default_cap_time_hours / 24)} day(s) + ${form.values.default_cap_time_hours % 24} hour(s)`}
                  </Text>
                </Stack>
              </Paper>
            </Grid.Col>
          </Grid>
        </Stack>
      </Paper>

      {settings && (
        <Paper withBorder p="md">
          <Stack gap="xs">
            <Text fw={600} size="sm">Settings History</Text>
            <Text size="xs" c="dimmed">
              Last updated: {new Date(settings.updated_at).toLocaleString('en-IN')}
            </Text>
            <Text size="xs" c="dimmed">
              Created: {new Date(settings.created_at).toLocaleString('en-IN')}
            </Text>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};

export default BatchSettings;